import { hashString, smoothNoise2D } from "../core/random";

export type SurfaceKind = "stone" | "earth" | "grass" | "water";
export type TownFeature =
  | "home-courtyard"
  | "main-canal"
  | "branch-canal"
  | "bridge"
  | "revetment"
  | "quay"
  | "canal-street"
  | "alley"
  | "square"
  | "garden"
  | "building-plot";
export type WaterwayKind = "main-canal" | "branch-canal";
export type WalkwayKind = "home" | "bridge" | "quay" | "canal-street" | "alley" | "square";
export type CardinalAxis = "x" | "z";

export interface Point2 {
  x: number;
  z: number;
}

export interface WorldBounds {
  minX: number;
  minZ: number;
  size: number;
}

export interface CanalSemantic {
  id: string;
  kind: WaterwayKind;
  axis: CardinalAxis;
  index: number;
  center: number;
  distance: number;
  halfWidth: number;
  waterHeight: number;
}

export interface BridgeSemantic extends Point2 {
  id: string;
  axis: CardinalAxis;
  width: number;
  length: number;
  waterway: WaterwayKind;
  deckHeight: number;
}

export interface TownSemantic {
  feature: TownFeature;
  density: number;
  walkable: boolean;
  waterway: CanalSemantic | null;
  walkway: WalkwayKind | null;
  bridge: BridgeSemantic | null;
}

export interface WorldPoint extends TownSemantic {
  height: number;
  waterDepth: number;
  waterHeight: number | null;
  surface: SurfaceKind;
}

const MAIN_CANAL_SPACING = 84;
const BRANCH_CANAL_SPACING = 56;
const MAIN_CANAL_HALF_WIDTH = 5.1;
const BRANCH_CANAL_HALF_WIDTH = 3.2;
const REVETMENT_WIDTH = 1.15;
const CANAL_STREET_WIDTH = 3.4;
const ALLEY_HALF_WIDTH = 1.75;
const HOME_HEIGHT = 0.92;
const HOME_BOUNDS = { minX: -11.5, maxX: 11.5, minZ: -3.5, maxZ: 20.5 } as const;

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}

function smoothstep(edge0: number, edge1: number, value: number): number {
  const amount = clamp01((value - edge0) / (edge1 - edge0));
  return amount * amount * (3 - 2 * amount);
}

function signedUnit(seed: string): number {
  return (hashString(seed) / 0xffffffff) * 2 - 1;
}

function unit(seed: string): number {
  return hashString(seed) / 0xffffffff;
}

export class JiangnanWorldModel {
  readonly seed: string;
  readonly version: string;
  readonly bridge: Point2;

  private readonly identity: string;
  private readonly pathPhase: number;
  private readonly waterPhase: number;
  private readonly heightSeed: string;
  private readonly detailSeed: string;
  private readonly mainOrigin: number;
  private readonly branchOrigin: number;
  private readonly homeMainIndex: number;
  private readonly homeAlleyIndex: number;

  constructor(seed: string, version = "jiangnan-v2") {
    this.seed = seed.trim() || "misty-water-town";
    this.version = version;
    this.identity = `${version}:${this.seed}`;
    this.pathPhase = signedUnit(`${this.identity}:path-phase`) * Math.PI;
    this.waterPhase = signedUnit(`${this.identity}:water-phase`) * Math.PI;
    this.heightSeed = `${this.identity}:height`;
    this.detailSeed = `${this.identity}:detail`;
    this.mainOrigin = 49 + signedUnit(`${this.identity}:main-origin`) * 4.5;
    this.branchOrigin = 29 + signedUnit(`${this.identity}:branch-origin`) * 3.5;
    this.homeMainIndex = Math.round((50 - this.mainOrigin) / MAIN_CANAL_SPACING);
    this.homeAlleyIndex = Math.round(-this.branchOrigin / BRANCH_CANAL_SPACING - 0.5);
    const homeBridge = this.createMainBridge(this.homeMainIndex, this.homeAlleyIndex);
    this.bridge = { x: homeBridge.x, z: homeBridge.z };
  }

  /** Compatibility centerline for the pedestrian spine through the home quarter. */
  pathCenterX(z: number): number {
    return this.verticalAlleyCenterX(this.homeAlleyIndex, z);
  }

  /** Compatibility centerline for the main canal nearest the home quarter. */
  waterCenterZ(x: number): number {
    return this.mainCanalCenterZ(this.homeMainIndex, x);
  }

  /** Compatibility height query for the main canal nearest home. */
  waterSurfaceHeight(x: number): number {
    return this.getWaterSurfaceHeight(x, this.waterCenterZ(x));
  }

  getWaterSurfaceHeight(x: number, z: number): number {
    return 0.43 + Math.sin(x * 0.005 + this.waterPhase) * 0.035 + Math.sin(z * 0.004 - this.waterPhase) * 0.025;
  }

  getPoint(x: number, z: number): WorldPoint {
    const semantic = this.getTownSemantic(x, z);
    if (semantic.feature === "home-courtyard") {
      return { ...semantic, height: HOME_HEIGHT, waterDepth: 0, waterHeight: null, surface: "stone" };
    }

    if (semantic.bridge) {
      const height = this.getBridgeDeckHeight(semantic.bridge, x, z);
      return {
        ...semantic,
        height,
        waterDepth: 0,
        waterHeight: this.getWaterSurfaceHeight(x, z),
        surface: "stone",
      };
    }

    if (semantic.waterway) {
      const { distance, halfWidth, kind, waterHeight } = semantic.waterway;
      const channel = 1 - smoothstep(0, halfWidth, distance);
      const depth = (kind === "main-canal" ? 0.36 + channel * 1.24 : 0.28 + channel * 0.82);
      return { ...semantic, height: waterHeight - depth, waterDepth: depth, waterHeight, surface: "water" };
    }

    const height = semantic.walkable ? this.getWalkwayHeight(x, z) : this.getLandHeight(x, z);
    const surface: SurfaceKind = semantic.walkable || semantic.feature === "revetment"
      ? "stone"
      : semantic.feature === "garden" && this.isGardenSoil(x, z)
        ? "earth"
        : "grass";
    return { ...semantic, height, waterDepth: 0, waterHeight: null, surface };
  }

  getTownSemantic(x: number, z: number): TownSemantic {
    const density = this.getTownDensity(x, z);
    if (this.isHomeCourtyard(x, z)) {
      return this.semantic("home-courtyard", density, true, null, "home", null);
    }

    const bridge = this.getBridgeAt(x, z);
    if (bridge) return this.semantic("bridge", density, true, null, "bridge", bridge);

    const canal = this.getCanalAt(x, z);
    if (canal) return this.semantic(canal.kind, density, false, canal, null, null);

    const bank = this.getNearestBank(x, z);
    if (bank && bank.offset <= REVETMENT_WIDTH) {
      const quay = this.isQuay(bank.canal, x, z);
      return this.semantic(quay ? "quay" : "revetment", density, quay, null, quay ? "quay" : null, null);
    }
    if (bank && bank.offset <= REVETMENT_WIDTH + CANAL_STREET_WIDTH) {
      return this.semantic("canal-street", density, true, null, "canal-street", null);
    }

    const alley = this.getAlleyDistances(x, z);
    const inVerticalAlley = alley.vertical <= ALLEY_HALF_WIDTH;
    const inHorizontalAlley = alley.horizontal <= ALLEY_HALF_WIDTH;
    if (inVerticalAlley && inHorizontalAlley && this.hasSquare(alley.verticalIndex, alley.horizontalIndex)) {
      const squareDistance = Math.hypot(alley.vertical, alley.horizontal);
      if (squareDistance <= 5.2) return this.semantic("square", density, true, null, "square", null);
    }
    if (inVerticalAlley || inHorizontalAlley) {
      return this.semantic("alley", density, true, null, "alley", null);
    }

    const feature: TownFeature = density < 0.46 || this.isPocketGarden(x, z) ? "garden" : "building-plot";
    return this.semantic(feature, density, false, null, null, null);
  }

  getTownFeature(x: number, z: number): TownFeature {
    return this.getTownSemantic(x, z).feature;
  }

  isWalkable(x: number, z: number): boolean {
    return this.getTownSemantic(x, z).walkable;
  }

  getCanalAt(x: number, z: number): CanalSemantic | null {
    const main = this.nearestMainCanal(x, z);
    const branch = this.nearestBranchCanal(x, z);
    const inMain = main.distance <= main.halfWidth;
    const inBranch = branch.distance <= branch.halfWidth;
    if (!inMain && !inBranch) return null;
    if (inMain && inBranch) {
      return main.distance / main.halfWidth <= branch.distance / branch.halfWidth ? main : branch;
    }
    return inMain ? main : branch;
  }

  getBridgeAt(x: number, z: number): BridgeSemantic | null {
    const mainIndex = Math.round((z - this.mainOrigin) / MAIN_CANAL_SPACING);
    const alleyIndex = Math.round((x - this.branchOrigin) / BRANCH_CANAL_SPACING - 0.5);
    const branchIndex = Math.round((x - this.branchOrigin) / BRANCH_CANAL_SPACING);
    const candidates: BridgeSemantic[] = [];
    for (let i = mainIndex - 1; i <= mainIndex + 1; i += 1) {
      for (let j = alleyIndex - 1; j <= alleyIndex + 1; j += 1) candidates.push(this.createMainBridge(i, j));
    }
    for (let j = branchIndex - 1; j <= branchIndex + 1; j += 1) {
      for (let i = mainIndex - 1; i <= mainIndex + 1; i += 1) {
        candidates.push(this.createBranchBridge(j, i, -1), this.createBranchBridge(j, i, 1));
      }
    }
    return candidates.find((candidate) => this.containsBridge(candidate, x, z)) ?? null;
  }

  getBridgesInBounds(bounds: WorldBounds): BridgeSemantic[] {
    const margin = 12;
    const maxX = bounds.minX + bounds.size;
    const maxZ = bounds.minZ + bounds.size;
    const mainStart = Math.floor((bounds.minZ - this.mainOrigin - margin) / MAIN_CANAL_SPACING) - 1;
    const mainEnd = Math.ceil((maxZ - this.mainOrigin + margin) / MAIN_CANAL_SPACING) + 1;
    const branchStart = Math.floor((bounds.minX - this.branchOrigin - margin) / BRANCH_CANAL_SPACING) - 1;
    const branchEnd = Math.ceil((maxX - this.branchOrigin + margin) / BRANCH_CANAL_SPACING) + 1;
    const bridges: BridgeSemantic[] = [];

    for (let i = mainStart; i <= mainEnd; i += 1) {
      for (let j = branchStart - 1; j <= branchEnd; j += 1) {
        const bridge = this.createMainBridge(i, j);
        if (this.inExpandedBounds(bridge, bounds, margin)) bridges.push(bridge);
      }
    }
    for (let j = branchStart; j <= branchEnd; j += 1) {
      for (let i = mainStart; i <= mainEnd; i += 1) {
        for (const side of [-1, 1] as const) {
          const bridge = this.createBranchBridge(j, i, side);
          if (this.inExpandedBounds(bridge, bounds, margin)) bridges.push(bridge);
        }
      }
    }
    return bridges.sort((a, b) => a.id.localeCompare(b.id));
  }

  getHeight(x: number, z: number): number {
    return this.getPoint(x, z).height;
  }

  getWaterDepth(x: number, z: number): number {
    return this.getPoint(x, z).waterDepth;
  }

  getSurfaceKind(x: number, z: number): SurfaceKind {
    return this.getPoint(x, z).surface;
  }

  /** Retained for callers migrating from v1. Water-town blocks no longer generate paddies. */
  isPaddy(_x: number, _z: number): boolean {
    return false;
  }

  private semantic(
    feature: TownFeature,
    density: number,
    walkable: boolean,
    waterway: CanalSemantic | null,
    walkway: WalkwayKind | null,
    bridge: BridgeSemantic | null,
  ): TownSemantic {
    return { feature, density, walkable, waterway, walkway, bridge };
  }

  private nearestMainCanal(x: number, z: number): CanalSemantic {
    const estimate = Math.round((z - this.mainOrigin) / MAIN_CANAL_SPACING);
    let bestIndex = estimate;
    let bestCenter = this.mainCanalCenterZ(estimate, x);
    let bestDistance = Math.abs(z - bestCenter);
    for (let index = estimate - 1; index <= estimate + 1; index += 1) {
      const center = this.mainCanalCenterZ(index, x);
      const distance = Math.abs(z - center);
      if (distance < bestDistance) {
        bestIndex = index;
        bestCenter = center;
        bestDistance = distance;
      }
    }
    return {
      id: `main-${bestIndex}`,
      kind: "main-canal",
      axis: "x",
      index: bestIndex,
      center: bestCenter,
      distance: bestDistance,
      halfWidth: this.mainCanalHalfWidth(bestIndex),
      waterHeight: this.getWaterSurfaceHeight(x, z),
    };
  }

  private nearestBranchCanal(x: number, z: number): CanalSemantic {
    const estimate = Math.round((x - this.branchOrigin) / BRANCH_CANAL_SPACING);
    let bestIndex = estimate;
    let bestCenter = this.branchCanalCenterX(estimate, z);
    let bestDistance = Math.abs(x - bestCenter);
    for (let index = estimate - 1; index <= estimate + 1; index += 1) {
      const center = this.branchCanalCenterX(index, z);
      const distance = Math.abs(x - center);
      if (distance < bestDistance) {
        bestIndex = index;
        bestCenter = center;
        bestDistance = distance;
      }
    }
    return {
      id: `branch-${bestIndex}`,
      kind: "branch-canal",
      axis: "z",
      index: bestIndex,
      center: bestCenter,
      distance: bestDistance,
      halfWidth: this.branchCanalHalfWidth(bestIndex),
      waterHeight: this.getWaterSurfaceHeight(x, z),
    };
  }

  private getNearestBank(x: number, z: number): { canal: CanalSemantic; offset: number } | null {
    const main = this.nearestMainCanal(x, z);
    const branch = this.nearestBranchCanal(x, z);
    const mainOffset = main.distance - main.halfWidth;
    const branchOffset = branch.distance - branch.halfWidth;
    if (mainOffset < 0 || branchOffset < 0) return null;
    return mainOffset <= branchOffset ? { canal: main, offset: mainOffset } : { canal: branch, offset: branchOffset };
  }

  private getAlleyDistances(x: number, z: number): {
    vertical: number;
    horizontal: number;
    verticalIndex: number;
    horizontalIndex: number;
  } {
    const verticalIndex = Math.round((x - this.branchOrigin) / BRANCH_CANAL_SPACING - 0.5);
    const horizontalIndex = Math.round((z - this.mainOrigin) / MAIN_CANAL_SPACING - 0.5);
    return {
      vertical: Math.abs(x - this.verticalAlleyCenterX(verticalIndex, z)),
      horizontal: Math.abs(z - this.horizontalAlleyCenterZ(horizontalIndex, x)),
      verticalIndex,
      horizontalIndex,
    };
  }

  private mainCanalCenterZ(index: number, x: number): number {
    const phase = this.waterPhase + index * 0.91;
    return this.mainOrigin + index * MAIN_CANAL_SPACING + Math.sin(x * 0.012 + phase) * 2.4 + Math.sin(x * 0.003 - phase) * 1.15;
  }

  private branchCanalCenterX(index: number, z: number): number {
    const phase = this.pathPhase - index * 0.73;
    return this.branchOrigin + index * BRANCH_CANAL_SPACING + Math.sin(z * 0.015 + phase) * 1.7 + Math.sin(z * 0.004 + phase) * 0.8;
  }

  private verticalAlleyCenterX(index: number, z: number): number {
    const phase = this.pathPhase + index * 0.47;
    return this.branchOrigin + (index + 0.5) * BRANCH_CANAL_SPACING + Math.sin(z * 0.012 + phase) * 1.15;
  }

  private horizontalAlleyCenterZ(index: number, x: number): number {
    const phase = this.waterPhase - index * 0.59;
    return this.mainOrigin + (index + 0.5) * MAIN_CANAL_SPACING + Math.sin(x * 0.01 + phase) * 1.4;
  }

  private mainCanalHalfWidth(index: number): number {
    return MAIN_CANAL_HALF_WIDTH + signedUnit(`${this.detailSeed}:main-width:${index}`) * 0.5;
  }

  private branchCanalHalfWidth(index: number): number {
    return BRANCH_CANAL_HALF_WIDTH + signedUnit(`${this.detailSeed}:branch-width:${index}`) * 0.38;
  }

  private createMainBridge(mainIndex: number, alleyIndex: number): BridgeSemantic {
    let x = this.verticalAlleyCenterX(alleyIndex, this.mainOrigin + mainIndex * MAIN_CANAL_SPACING);
    let z = this.mainCanalCenterZ(mainIndex, x);
    for (let iteration = 0; iteration < 4; iteration += 1) {
      x = this.verticalAlleyCenterX(alleyIndex, z);
      z = this.mainCanalCenterZ(mainIndex, x);
    }
    const length = this.mainCanalHalfWidth(mainIndex) * 2 + REVETMENT_WIDTH * 2 + 0.8;
    return {
      id: `bridge-main-${mainIndex}-${alleyIndex}`,
      x,
      z,
      axis: "z",
      width: 4.8,
      length,
      waterway: "main-canal",
      deckHeight: this.getWaterSurfaceHeight(x, z) + 0.96,
    };
  }

  private createBranchBridge(branchIndex: number, mainIndex: number, side: -1 | 1): BridgeSemantic {
    let x = this.branchOrigin + branchIndex * BRANCH_CANAL_SPACING;
    let z = this.mainCanalCenterZ(mainIndex, x) + side * (
      this.mainCanalHalfWidth(mainIndex) + REVETMENT_WIDTH + CANAL_STREET_WIDTH * 0.5
    );
    for (let iteration = 0; iteration < 4; iteration += 1) {
      x = this.branchCanalCenterX(branchIndex, z);
      z = this.mainCanalCenterZ(mainIndex, x) + side * (
        this.mainCanalHalfWidth(mainIndex) + REVETMENT_WIDTH + CANAL_STREET_WIDTH * 0.5
      );
    }
    const length = this.branchCanalHalfWidth(branchIndex) * 2 + REVETMENT_WIDTH * 2 + 0.8;
    return {
      id: `bridge-branch-${branchIndex}-${mainIndex}-${side < 0 ? "north" : "south"}`,
      x,
      z,
      axis: "x",
      width: 4.4,
      length,
      waterway: "branch-canal",
      deckHeight: this.getWaterSurfaceHeight(x, z) + 0.82,
    };
  }

  private containsBridge(bridge: BridgeSemantic, x: number, z: number): boolean {
    const along = bridge.axis === "x" ? Math.abs(x - bridge.x) : Math.abs(z - bridge.z);
    const across = bridge.axis === "x" ? Math.abs(z - bridge.z) : Math.abs(x - bridge.x);
    return along <= bridge.length * 0.5 && across <= bridge.width * 0.5;
  }

  private getBridgeDeckHeight(bridge: BridgeSemantic, x: number, z: number): number {
    const along = bridge.axis === "x" ? Math.abs(x - bridge.x) : Math.abs(z - bridge.z);
    const normalized = clamp01(along / (bridge.length * 0.5));
    const rise = bridge.waterway === "main-canal" ? 0.46 : 0.34;
    return bridge.deckHeight - rise * Math.sin(normalized * Math.PI * 0.5);
  }

  private getTownDensity(x: number, z: number): number {
    const blockX = Math.floor((x - this.branchOrigin) / BRANCH_CANAL_SPACING);
    const blockZ = Math.floor((z - this.mainOrigin) / MAIN_CANAL_SPACING);
    const block = unit(`${this.detailSeed}:density:${blockX}:${blockZ}`);
    const local = smoothNoise2D(`${this.detailSeed}:density-detail`, x / 38, z / 38);
    return 0.38 + block * 0.48 + local * 0.12;
  }

  private getLandHeight(x: number, z: number): number {
    const broad = smoothNoise2D(this.heightSeed, x / 130, z / 130) - 0.5;
    const detail = smoothNoise2D(`${this.heightSeed}:detail`, x / 36, z / 36) - 0.5;
    return 0.84 + broad * 0.3 + detail * 0.1;
  }

  private getWalkwayHeight(x: number, z: number): number {
    const broad = smoothNoise2D(`${this.heightSeed}:walkway`, x / 145, z / 145) - 0.5;
    return 0.9 + broad * 0.16;
  }

  private isHomeCourtyard(x: number, z: number): boolean {
    return x >= HOME_BOUNDS.minX && x <= HOME_BOUNDS.maxX && z >= HOME_BOUNDS.minZ && z <= HOME_BOUNDS.maxZ;
  }

  private isQuay(canal: CanalSemantic, x: number, z: number): boolean {
    const along = canal.axis === "x" ? x : z;
    const side = canal.axis === "x" ? Math.sign(z - canal.center) : Math.sign(x - canal.center);
    const segment = Math.floor(along / 18);
    const selectedSide = hashString(`${this.detailSeed}:quay-side:${canal.id}:${segment}`) % 2 === 0 ? -1 : 1;
    return side === selectedSide && hashString(`${this.detailSeed}:quay:${canal.id}:${segment}`) % 100 < 42;
  }

  private hasSquare(verticalIndex: number, horizontalIndex: number): boolean {
    return hashString(`${this.detailSeed}:square:${verticalIndex}:${horizontalIndex}`) % 100 < 58;
  }

  private isPocketGarden(x: number, z: number): boolean {
    const cellX = Math.floor(x / 14);
    const cellZ = Math.floor(z / 14);
    return hashString(`${this.detailSeed}:garden:${cellX}:${cellZ}`) % 100 < 9;
  }

  private isGardenSoil(x: number, z: number): boolean {
    const cellX = Math.floor(x / 9);
    const cellZ = Math.floor(z / 9);
    return hashString(`${this.detailSeed}:soil:${cellX}:${cellZ}`) % 100 < 20;
  }

  private inExpandedBounds(point: Point2, bounds: WorldBounds, margin: number): boolean {
    return point.x >= bounds.minX - margin
      && point.x <= bounds.minX + bounds.size + margin
      && point.z >= bounds.minZ - margin
      && point.z <= bounds.minZ + bounds.size + margin;
  }
}

export const JIANGNAN_PATH_HALF_WIDTH = ALLEY_HALF_WIDTH;
export const JIANGNAN_WATER_HALF_WIDTH = MAIN_CANAL_HALF_WIDTH;
export const JIANGNAN_MAIN_CANAL_SPACING = MAIN_CANAL_SPACING;
export const JIANGNAN_BRANCH_CANAL_SPACING = BRANCH_CANAL_SPACING;
